import React, { useEffect, useState } from "react";
import { Box, CircularProgress, useTheme, Avatar as MUIAvatar } from "@mui/material";
import Icon from "../NGMaterialIcon/NGMaterialIcon";
import { MUIImageProps } from "./MUIImage.types";
import { getInitials } from "../../library/utils";
import { log } from "../../library/logger";

const tag = "NGImage";

const MUIImage = ({
  src,
  alt = "",
  height = "100%",
  width = "100%",
  duration = 250,
  easing = "cubic-bezier(0.2, 0, 0, 1)", // Standard easing per MD3
  showLoading = false,
  errorIcon = true,
  distance = 0,
  shift = false,
  shiftDuration,
  style = {},
  sx = {},
  bgColor = "transparent",
  fit = "cover",
  position = "relative",
  className,
  wrapperClassName,
  iconWrapperClassName,
  wrapperStyle = {},
  iconWrapperStyle = {},
  onError,
  dataTestId,
  ...otherProps
}: MUIImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const theme = useTheme();
  const shiftValue = typeof distance === "number" ? `${distance}px` : distance;
  const shiftDirections = {
    left: `translateX(-${shiftValue})`,
    right: `translateX(${shiftValue})`,
    top: `translateY(-${shiftValue})`,
    bottom: `translateY(${shiftValue})`,
  };

  const isValidImage = (src: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        setError(false);
        resolve(true);
      };
      image.onerror = () => {
        setError(true);
        resolve(false);
      };
      image.src = src;
    });
  };

  const handleImageLoad = () => {
    setLoaded(true);
    setError(false);
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setError(true);
    setLoaded(false);
    log.error(tag, "Failed to load image", event);
    if (onError) {
      onError(event);
      log.error(tag, "MUIImage: Custom onError handler invoked.");
    }
  };

  useEffect(() => {
    const checkImage = async () => {
      const validImage = await isValidImage(src);
      if (!validImage) {
        setError(true);
      }
    };

    checkImage();
  }, [src]);

  const transformValue = shift && !loaded && typeof shift === "string" ? shiftDirections[shift] || "none" : "none";

  const imageStyles = {
    opacity: loaded ? 1 : 0,
    transition: theme.transitions.create(["opacity", "transform"], {
      duration: shiftDuration || duration,
      easing: easing,
    }),
    transform: transformValue,
    height: "100%",
    width: "100%",
    objectFit: fit,
    ...style,
  };
  return (
    <Box
      className={wrapperClassName}
      position={(theme) => theme.breakpoints.values[position]}
      height={height}
      width={width}
      overflow="hidden"
      sx={{ bgcolor: bgColor, ...wrapperStyle }}
      data-testid={dataTestId || otherProps["data-testid"]}
    >
      {!loaded && showLoading && !error && (
        <Box
          className={iconWrapperClassName}
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            ...iconWrapperStyle,
          }}
        >
          {showLoading === true ? <CircularProgress /> : showLoading}
        </Box>
      )}
      {error && (
        <Box
          className={iconWrapperClassName}
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            ...iconWrapperStyle,
          }}
        >
          {React.isValidElement(errorIcon) ? (
            errorIcon
          ) : !alt ? (
            <Icon name="broken_image" style={{ color: "gray", fontSize: "48px" }} />
          ) : (
            <MUIAvatar
              alt={alt}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: "gray",
                ...iconWrapperStyle,
                ...wrapperStyle,
              }}
            >
              {getInitials(alt)}
            </MUIAvatar>
          )}
        </Box>
      )}
      {!error && (
        <Box
          component="img"
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          className={className}
          sx={{ ...imageStyles, ...sx }}
          {...otherProps}
        />
      )}
    </Box>
  );
};

export { MUIImage };
